import React, { useEffect, useMemo, useState } from "react";
import { InputAdornment, MenuItem, Select, TextField } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import { extractFilterListPayload } from "../../hooks/useAdvancedSearchFilterOptions";
import { useCachedFilterOptions } from "../../hooks/useCachedFilterOptions";
import {
  EMPTY_PRODUTOS_FILTER_STATE,
  normalizeProdutosFilterState,
} from "../../utils/dashboardProdutosFilterState";
import DashboardSavedFiltersPanel from "./DashboardSavedFiltersPanel";
import {
  DashboardFilterModalActions,
  DashboardFilterModalFrame,
  DashboardFilterSection,
  DashboardMultiPickList,
} from "./dashboardFilterModalUi";

async function fetchProdutosFilterOptions() {
  const cat = await fetchData("/categories?page=1&limit=200", "GET");
  return { allCategories: extractFilterListPayload(cat) };
}

export default function DashboardProdutosFiltersModal({
  open,
  onClose,
  initialState = EMPTY_PRODUTOS_FILTER_STATE,
  onApply,
}) {
  const { t } = useTranslation("settings");
  const { data: options, isLoading: loading } = useCachedFilterOptions(
    "dashboard-produtos",
    fetchProdutosFilterOptions,
    { enabled: open }
  );
  const allCategories = options?.allCategories || [];
  const [categoryIds, setCategoryIds] = useState([]);
  const [subcategoryIds, setSubcategoryIds] = useState([]);
  const [availability, setAvailability] = useState("");
  const [search, setSearch] = useState("");
  const prevOpen = React.useRef(false);

  const categories = useMemo(
    () => allCategories.filter((c) => !Number(c?.id_pai || 0)),
    [allCategories]
  );

  const subcategories = useMemo(() => {
    const all = allCategories.filter((c) => Number(c?.id_pai || 0) > 0);
    if (!categoryIds.length) return all;
    const parents = new Set(categoryIds.map((id) => Number(id)));
    return all.filter((c) => parents.has(Number(c?.id_pai)));
  }, [allCategories, categoryIds]);

  useEffect(() => {
    if (!open) {
      prevOpen.current = false;
      return;
    }
    const opening = !prevOpen.current;
    prevOpen.current = true;
    if (opening) {
      const s = normalizeProdutosFilterState(initialState);
      setCategoryIds(s.ids_categorias);
      setSubcategoryIds(s.ids_subcategorias);
      setAvailability(s.availability);
      setSearch(s.search);
    }
  }, [open, initialState]);

  useEffect(() => {
    if (!categoryIds.length) return;
    const parents = new Set(categoryIds.map((id) => Number(id)));
    const allowedIds = new Set(
      allCategories
        .filter((c) => Number(c?.id_pai || 0) > 0 && parents.has(Number(c?.id_pai)))
        .map((c) => Number(c?.id))
    );
    setSubcategoryIds((prev) => prev.filter((id) => allowedIds.has(Number(id))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIds, allCategories]);

  const buildLabels = () => {
    const cats = {};
    const subs = {};
    allCategories.forEach((c) => {
      const id = Number(c?.id);
      if (!id) return;
      const label = String(c?.nome || c?.name || `#${id}`);
      if (Number(c?.id_pai || 0) > 0) subs[String(id)] = label;
      else cats[String(id)] = label;
    });
    return { categories: cats, subcategories: subs };
  };

  const handleClear = () => {
    setCategoryIds([]);
    setSubcategoryIds([]);
    setAvailability("");
    setSearch("");
  };

  const handleApply = () => {
    onApply?.(
      normalizeProdutosFilterState({
        ids_categorias: categoryIds,
        ids_subcategorias: subcategoryIds,
        availability,
        search,
        labels: buildLabels(),
      })
    );
    onClose?.();
  };

  const hasActive =
    categoryIds.length > 0 ||
    subcategoryIds.length > 0 ||
    Boolean(availability) ||
    Boolean(search.trim());

  return (
    <DashboardFilterModalFrame
      open={open}
      onClose={onClose}
      icon={<Inventory2OutlinedIcon />}
      title={t("dashboardPage.produtosFilters.title", { defaultValue: "Filtrar produtos" })}
      subtitle={t("dashboardPage.produtosFilters.subtitle", {
        defaultValue: "Catálogo e conversão consideram só este recorte",
      })}
      loading={loading}
      footer={
        <DashboardFilterModalActions
          onClear={handleClear}
          onClose={onClose}
          onApply={handleApply}
          hasActive={hasActive}
          clearLabel={t("dashboardPage.produtosFilters.clearAll", { defaultValue: "Limpar" })}
          cancelLabel={t("dashboardPage.produtosFilters.cancel", { defaultValue: "Cancelar" })}
          applyLabel={t("dashboardPage.produtosFilters.apply", { defaultValue: "Aplicar" })}
        />
      }
    >
      <DashboardFilterSection
        title={t("dashboardPage.produtosFilters.categories", { defaultValue: "Categoria" })}
        selectedCount={categoryIds.length}
      >
        <DashboardMultiPickList
          options={categories}
          value={categoryIds}
          onChange={setCategoryIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.produtosFilters.searchCategories", {
            defaultValue: "Buscar categorias…",
          })}
          emptyLabel={t("dashboardPage.produtosFilters.emptyCategories", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.produtosFilters.subcategories", { defaultValue: "Subcategoria" })}
        selectedCount={subcategoryIds.length}
      >
        <DashboardMultiPickList
          options={subcategories}
          value={subcategoryIds}
          onChange={setSubcategoryIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.produtosFilters.searchSubcategories", {
            defaultValue: "Buscar subcategorias…",
          })}
          emptyLabel={t("dashboardPage.produtosFilters.emptySubcategories", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.produtosFilters.availability", { defaultValue: "Disponibilidade" })}
      >
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={availability}
          onChange={(e) => setAvailability(e.target.value)}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="">
            {t("dashboardPage.produtosFilters.availabilityAll", { defaultValue: "Qualquer" })}
          </MenuItem>
          <MenuItem value="imediata">
            {t("dashboardPage.produtosFilters.availabilityImmediate", {
              defaultValue: "Disponibilidade imediata",
            })}
          </MenuItem>
          <MenuItem value="sob-encomenda">
            {t("dashboardPage.produtosFilters.availabilityOrder", {
              defaultValue: "Sob encomenda",
            })}
          </MenuItem>
        </Select>
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.produtosFilters.search", { defaultValue: "Busca" })}
        hint={t("dashboardPage.produtosFilters.searchHint", { defaultValue: "Nome ou SKU" })}
      >
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("dashboardPage.produtosFilters.searchPlaceholder", {
            defaultValue: "Nome ou código do produto…",
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 },
          }}
        />
      </DashboardFilterSection>

      {!loading ? (
        <DashboardSavedFiltersPanel
          section="produtos"
          getSnapshot={() =>
            normalizeProdutosFilterState({
              ids_categorias: categoryIds,
              ids_subcategorias: subcategoryIds,
              availability,
              search,
              labels: buildLabels(),
            })
          }
          onApply={(state) => {
            onApply?.(state);
            onClose?.();
          }}
          canSave
        />
      ) : null}
    </DashboardFilterModalFrame>
  );
}
